import * as d3 from 'd3';

import { Size, Margin } from '../../visTypes';

import { wrapLine, exportSvgAsPng, ISharedD3ChartProps } from '../../vis-shared';

export interface IPieBarItem {
  color: string;
  label: string;
  value?: number;
}

export interface IPieBarConfig extends ISharedD3ChartProps {
  target: any;
  series: IPieBarItem[];
  valueAccessor?: (datum: IPieBarItem) => number;
  valueFormatter?: (value: number, percentage: number) => string;
  width?: number;
  height?: number;
  barHeight?: number;
  labelFontSize?: number;
}

export interface ILayoutItem extends IPieBarItem {
  cumulative: number;
  percentage: number;
  x: number;
  y: number;
  xAnchor: number;
  labelGroup: d3.Selection<SVGGElement, any, any, any>;
  labelWidth: number;
}

function drawPath(x1: number, y1: number, x2: number, y2: number) {
  const breadcrumbs = [];
  breadcrumbs.push(`M${x1},${y1}`);
  breadcrumbs.push(`L${x1}, ${y1 - 20}`);
  breadcrumbs.push(`L${x2}, ${y1 - 20}`);
  breadcrumbs.push(`L${x2}, ${y2}`);
  return breadcrumbs.join(' ');
}

export class PieBarLabeled {
  target: any;
  series: IPieBarItem[];
  layoutSeries!: ILayoutItem[];
  valueAccessor: (d: IPieBarItem) => number;
  valueFormatter: (value: number, percentage: number) => string;
  size: Size;
  chartSize: Size;
  margin: Margin;
  labelFontSize: number;
  svg!: d3.Selection<any, any, any, any>;
  chartSelection!: d3.Selection<SVGGElement, any, any, any>;
  labelsSelection!: d3.Selection<SVGGElement, any, any, any>;
  annotationSelection!: d3.Selection<SVGGElement, any, any, any>;
  xScale!: d3.ScaleLinear<number, number>;
  exportTitle: string | undefined;
  exportFileName: string;

  constructor(config: IPieBarConfig) {
    this.target = config.target;
    this.series = config.series;
    this.valueAccessor = config.valueAccessor || ((d: IPieBarItem) => d.value || 0);
    this.valueFormatter = config.valueFormatter || ((value: number, percentage: number) => `${d3.format('.1f')(percentage)}%`);
    this.exportTitle = config.exportTitle;
    this.exportFileName = config.exportFileName || 'Distribution';
    const domWidth = Math.floor(parseInt(d3.select(this.target).style('width'), 10));
    const width = config.width || domWidth;
    const height = config.height || 100;
    const barHeight = config.barHeight || 24;
    this.labelFontSize = config.labelFontSize || 14;
    this.size = {
      width,
      height,
    };
    this.margin = {
      top: height - barHeight,
      bottom: 0,
      left: 5,
      right: 5,
    };
    this.chartSize = {
      width: width - this.margin.left - this.margin.right,
      height: height - this.margin.top - this.margin.bottom,
    };

    this.draw();
  }

  draw(): void {
    // initial drawing of SVG components
    if (!this.svg) {
      this.svg = d3.select(this.target).append('svg').classed('labeled-bar', true).attr('height', this.size.height).attr('width', this.size.width);
      this.chartSelection = this.svg.append('g').attr('transform', `translate(${this.margin.left}, ${this.margin.top})`);
      this.annotationSelection = this.svg.append('g').attr('transform', `translate(${this.margin.left}, ${this.margin.top})`);
      this.labelsSelection = this.svg.append('g').attr('transform', `translate(${this.margin.left}, ${this.margin.top})`);
    }
    this.layout();
    this.drawBars();
    this.drawLabels();
  }

  layout(): void {
    this.xScale = d3.scaleLinear().domain([0, 100]).range([0, this.chartSize.width]);
    const seriesTotal = d3.sum(this.series, this.valueAccessor);
    let cumulative = 0;
    this.layoutSeries = this.series.map((item) => {
      const normalizedPercentage = (this.valueAccessor(item) / seriesTotal) * 100;
      const itemCumulative = cumulative;
      cumulative += normalizedPercentage;
      const labelGroup = this.labelsSelection.append('g').attr('class', 'label').style('font-size', this.labelFontSize);
      labelGroup
        .append('text')
        .attr('class', 'label-text')
        .text(item.label)
        .attr('dy', '0.35em')
        .attr('y', 10)
        .attr('x', 16)
        .style('font-weight', 600)
        .attr('fill', '#1D1F27');
      labelGroup
        .append('text')
        .attr('class', 'percentage-text')
        .text(`${this.valueFormatter(this.valueAccessor(item), normalizedPercentage)}`)
        .style('fill', '#6B6157')
        .attr('y', 10 + this.labelFontSize * 1.5)
        .style('font-weight', 600)
        .attr('x', 16)
        .attr('dy', '0.35em');
      labelGroup
        .append('circle')
        .attr('r', 8)
        .attr('cx', 0)
        .attr('cy', 10)
        // .style('fill', '#0A71D0')
        .style('fill', item.color)
        .style('stroke', 'white')
        .style('stroke-width', 3);
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      const labelWidth = labelGroup.node()!.getBBox().width + 24;
      // const xAnchor = this.xScale(cumulative - normalizedPercentage / 2) + labelWidth / 2; // center of the bar to match left side of label
      const xAnchor = this.xScale(cumulative - normalizedPercentage * 0.9) + labelWidth / 2;
      return {
        ...item,
        cumulative: itemCumulative,
        labelGroup,
        xAnchor,
        percentage: normalizedPercentage,
        labelWidth,
        x: xAnchor,
        y: 0,
      };
    });
    // if total label width is more than allowed, abbreviate all text to same length
    const totalLabelWidth = this.layoutSeries.reduce((acc, d) => d.labelWidth + acc, 0);
    if (totalLabelWidth > this.chartSize.width) {
      this.layoutSeries.forEach((d) => {
        d.labelWidth = this.chartSize.width / this.layoutSeries.length;
        d.xAnchor = this.xScale(d.cumulative - d.percentage / 2) + d.labelWidth / 2; // center of the bar to match left side of label
      });
      /* eslint-disable-next-line @typescript-eslint/no-this-alias */
      const self = this;
      /* eslint-disable func-names */
      this.labelsSelection
        .selectAll('text.label-text')
        .each(function (d, i) {
          const item = self.layoutSeries[i];
          // @ts-ignore
          wrapLine(d3.select(this), item.labelWidth - 20);
        })
        .append('title')
        .text((d, i) => this.layoutSeries[i].label);
    }

    const boundedTick = (simulation: d3.Simulation<ILayoutItem, any>) => {
      simulation.tick();
      simulation.nodes().forEach((d: ILayoutItem) => {
        d.x = Math.max(d.labelWidth / 2, Math.min(this.chartSize.width - d.labelWidth / 2, d.x));
        d.y = 0;
      });
    };
    const simulation = d3
      .forceSimulation(this.layoutSeries)
      .force('x', d3.forceX((d: ILayoutItem) => d.xAnchor).strength(0.1))
      .force('y', d3.forceY(0).strength(1))
      .force(
        'collide',
        d3
          .forceCollide()
          .radius((d: any) => d.labelWidth / 2)
          .strength(1)
          .iterations(3)
      )
      .stop();

    for (let i = 0; i < 200; ++i) {
      boundedTick(simulation);
    }
    this.layoutSeries.forEach((item) => {
      item.labelGroup.attr('transform', `translate(${item.x - item.labelWidth / 2}, ${-this.margin.top})`);
    });
  }

  drawBars(): void {
    const randomId = Math.ceil(Math.random() * 1e5);
    const defs = this.svg.append('defs');
    defs
      .append('clipPath')
      .attr('id', `labeled-bar-clip-${randomId}`)
      .append('rect')
      .attr('width', this.chartSize.width)
      .attr('height', this.chartSize.height)
      .attr('rx', 4)
      .attr('ry', 4);
    this.chartSelection
      .attr('clip-path', `url(#labeled-bar-clip-${randomId})`)
      .selectAll('rect.bar')
      .data(this.layoutSeries)
      .join('rect')
      .attr('class', 'bar')
      .attr('x', (d: ILayoutItem) => this.xScale(d.cumulative)) // ILayoutItem
      .attr('width', (d: ILayoutItem) => this.xScale(d.percentage)) // ILayoutItem
      .attr('height', this.chartSize.height)
      .style('fill', (d: ILayoutItem) => d.color); // ILayoutItem
  }

  drawLabels(): void {
    this.layoutSeries.forEach((item) => {
      const y2 = -this.margin.top + 15;
      const leftBound = this.xScale(item.cumulative);
      const rightBound = this.xScale(item.cumulative + item.percentage);
      const labelPlacementLeft = item.x - item.labelWidth / 2;
      if (labelPlacementLeft < leftBound) {
        const sourceX = this.xScale(item.cumulative) + this.xScale(item.percentage) * 0.2;
        this.annotationSelection
          .append('path')
          .attr('d', drawPath(sourceX, 10, labelPlacementLeft, y2))
          .style('fill', 'none')
          .style('stroke-opacity', 0.2)
          .style('stroke', item.color)
          .style('stroke-width', 2);
      } else if (labelPlacementLeft > rightBound) {
        const sourceX = this.xScale(item.cumulative) + this.xScale(item.percentage) * 0.8;
        this.annotationSelection
          .append('path')
          .attr('d', drawPath(sourceX, 10, labelPlacementLeft, y2))
          .style('fill', 'none')
          .style('stroke', item.color)
          .style('stroke-opacity', 0.2)
          .style('stroke-width', 2);
      } else {
        this.annotationSelection
          .append('line')
          .attr('x1', labelPlacementLeft)
          .attr('x2', labelPlacementLeft)
          .attr('y1', 5)
          .attr('y2', y2)
          .style('stroke', item.color)
          .style('stroke-opacity', 0.2)
          .style('stroke-width', 2);
      }
    });
  }

  updateData(series: IPieBarItem[]): void {
    this.clear();
    this.series = series;
    this.draw();
  }

  clear(): void {
    d3.select(this.target).html('');
  }

  exportToPng(): void {
    const customCss = 'text { font-family: Lato; }';
    exportSvgAsPng(this.svg.node(), {
      customCss,
      scale: 3,
      backgroundColor: 'white',
      fileName: this.exportFileName,
      title: this.exportTitle,
    });
  }
}
